import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FlaskConical, Loader2, Save, Plus, Eye, X, UploadCloud, Trash2, History, Power } from "lucide-react";
import { API, authHeaders, uploadAdminFile } from "@/constants/roles";
import { TestPackView } from "@/components/admin/TestPackView";

const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";
const EMPTY = {
  title: "Melto Animation Test", enabled: false, instructions: "", script: "", audio_path: "",
  asset_paths: [], reference_urls: [], reference_files: [], required_length: "10–20 seconds",
  time_allowed: "", export_requirements: "", quality_requirements: "", additional_notes: "",
  payment_amount: 20, deadline_days: "",
};
const fromPack = (p) => ({
  ...EMPTY, ...p,
  audio_path: p.audio_path || "", asset_paths: p.asset_paths || [],
  reference_urls: p.reference_urls || [], reference_files: p.reference_files || [],
  payment_amount: p.payment_amount ?? 20, deadline_days: p.deadline_days ?? "",
});

const FileChip = ({ path, onRemove }) => (
  <span className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3 py-1 text-[11px] font-semibold text-[var(--pv-text-3)]">
    {path.split("/").pop()}
    <button type="button" onClick={onRemove} className="text-[var(--pv-text-4)] hover:text-rose-400"><X className="h-3 w-3" /></button>
  </span>
);

const TIERS = ["Standard Animator", "High-Quality Animator"];

export const AdminTestPack = () => {
  const [tier, setTier] = useState("Standard Animator");
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [refDraft, setRefDraft] = useState({ title: "", url: "" });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const audioRef = useRef(null);
  const assetRef = useRef(null);
  const refFileRef = useRef(null);

  const load = useCallback(() => {
    setData(null);
    axios.get(`${API}/staff/test-pack?tier=${encodeURIComponent(tier)}`, { headers: authHeaders() })
      .then((r) => {
        setData(r.data);
        if (r.data.current) setForm({ ...fromPack(r.data.current), tier });
        else setForm({ ...EMPTY, tier, title: tier === "High-Quality Animator" ? "Melto HQ Animation Test" : "Melto Animation Test" });
      })
      .catch(() => toast.error("Could not load the test pack."));
  }, [tier]);
  useEffect(load, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const err = (e, fallback) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

  const upload = async (file, apply) => {
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadAdminFile(file);
      apply(up.path);
      toast.success("File uploaded.");
    } catch (e) { err(e, "Upload failed."); }
    finally { setBusy(false); }
  };

  const saveInPlace = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/test-pack/current`, form, { headers: authHeaders() });
      toast.success("Test pack updated. In-flight applicants keep the version they started with.");
      load();
    } catch (e) {
      if (e.response?.status === 404) {
        try {
          await axios.post(`${API}/admin/test-pack`, form, { headers: authHeaders() });
          toast.success("Test pack v1 created.");
          load();
        } catch (e2) { err(e2, "Could not create the pack."); }
      } else err(e, "Could not save.");
    } finally { setBusy(false); }
  };

  const saveAsNewVersion = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/test-pack`, form, { headers: authHeaders() });
      toast.success(`Saved as version ${r.data.version} — new applicants get this version; in-flight tests keep theirs.`);
      load();
    } catch (e) { err(e, "Could not create new version."); }
    finally { setBusy(false); }
  };

  const toggleEnabled = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/test-pack/toggle`, { tier, enabled: !form.enabled }, { headers: authHeaders() });
      toast.success(form.enabled ? `${tier} test disabled — the apply form no longer offers it.` : `${tier} test enabled — applicants can now choose it.`);
      load();
    } catch (e) { err(e, "Toggle failed — create the pack first."); }
    finally { setBusy(false); }
  };

  const versions = data?.versions || [];

  return (
    <div data-testid="testpack-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-500/15 text-violet-300">
            <FlaskConical className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Animation Test Packs</h1>
            <p className="mt-1 text-sm text-[var(--pv-text-4)]">
              Separate hosted tests for Standard and HQ applicants. The agreed amount queues when a submitted test is accepted as a completed attempt — pass or fail.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="testpack-preview-btn" onClick={() => setPreview(true)} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-2 text-xs font-bold text-[var(--pv-text-2)] hover:text-cyan-400">
            <Eye className="h-3.5 w-3.5" /> Preview applicant view
          </button>
          <button data-testid="testpack-toggle-btn" onClick={toggleEnabled} disabled={busy || !data?.current} className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold disabled:opacity-50 ${form.enabled ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-300" : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-3)]"}`}>
            <Power className="h-3.5 w-3.5" /> {form.enabled ? "Enabled" : "Disabled"}
          </button>
        </div>
      </div>

      <div data-testid="testpack-tier-tabs" className="mt-5 flex gap-1.5">
        {TIERS.map((t) => (
          <button key={t} data-testid={`testpack-tier-${t === "High-Quality Animator" ? "hq" : "standard"}`} onClick={() => { setTier(t); setPreview(false); setShowVersions(false); }}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${tier === t ? (t === "High-Quality Animator" ? "bg-violet-500 text-white" : "bg-cyan-500 text-white") : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-3)]"}`}>
            {t} Test Pack
          </button>
        ))}
      </div>
      {!data?.current && data && (
        <p data-testid="testpack-empty-tier" className="mt-4 rounded-2xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] px-5 py-4 text-xs text-[var(--pv-text-4)]">
          No {tier} test pack yet — configure it below and save to create v1. The two tiers are fully independent; applicants always receive the pack matching their tier (or the tier you choose when requesting a test).
        </p>
      )}

      {versions.length > 0 && (
        <div className="mt-4">
          <button data-testid="testpack-versions-toggle" onClick={() => setShowVersions((v) => !v)} className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)] hover:text-cyan-400">
            <History className="h-3.5 w-3.5" /> Version history ({versions.length})
          </button>
          {showVersions && (
            <div data-testid="testpack-versions" className="mt-2 space-y-1.5">
              {versions.map((v) => (
                <div key={v.id} data-testid={`testpack-version-${v.version}`} className="flex items-center gap-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-2.5 text-xs">
                  <span className="font-bold text-[var(--pv-text)]">v{v.version}</span>
                  <span className="text-[var(--pv-text-4)]">{v.title}</span>
                  {v.is_current && <span className="rounded-full bg-cyan-500/15 border border-cyan-500/30 px-2 py-0.5 text-[10px] font-bold text-cyan-300">current</span>}
                  {v.enabled && <span className="rounded-full bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-bold text-emerald-300">enabled</span>}
                  <span className="ml-auto text-[10px] text-[var(--pv-text-4)]">{new Date(v.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-5 grid gap-4 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <input data-testid="pack-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Test title" className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="pack-length-input" value={form.required_length} onChange={(e) => set("required_length", e.target.value)} placeholder="Required length (e.g. 10–20 seconds)" className={inputCls} />
            <input data-testid="pack-time-input" value={form.time_allowed} onChange={(e) => set("time_allowed", e.target.value)} placeholder="Time allowed (e.g. 72 hours)" className={inputCls} />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="pack-payment-input" className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Agreed test payment (USD)</label>
            <input id="pack-payment-input" data-testid="pack-payment-input" type="number" min="0" step="0.01" value={form.payment_amount} onChange={(e) => set("payment_amount", parseFloat(e.target.value) || 0)} className={inputCls} />
            <p className="mt-1 text-[10px] text-[var(--pv-text-4)]">Queued as owed when a submitted test is accepted as a completed attempt — pass or fail.</p>
          </div>
          <div>
            <label htmlFor="pack-deadline-input" className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Deadline (days after sending)</label>
            <input id="pack-deadline-input" data-testid="pack-deadline-input" type="number" min="1" max="60" value={form.deadline_days} onChange={(e) => set("deadline_days", e.target.value ? parseInt(e.target.value, 10) : "")} placeholder="e.g. 3 (optional)" className={inputCls} />
            <p className="mt-1 text-[10px] text-[var(--pv-text-4)]">Shown to the applicant as a due date. Optional.</p>
          </div>
        </div>
        <textarea data-testid="pack-instructions-input" rows={4} value={form.instructions} onChange={(e) => set("instructions", e.target.value)} placeholder="Instructions — what to animate, scene direction, what to watch out for…" className={inputCls} />
        <textarea data-testid="pack-script-input" rows={5} value={form.script} onChange={(e) => set("script", e.target.value)} placeholder="Script the applicant must animate…" className={inputCls} />

        <div className="flex flex-wrap items-center gap-2">
          <button type="button" data-testid="pack-audio-upload" onClick={() => audioRef.current?.click()} disabled={busy} className="flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 px-4 py-2 text-xs font-bold text-violet-300 hover:bg-violet-500/20 disabled:opacity-50">
            <UploadCloud className="h-3.5 w-3.5" /> {form.audio_path ? "Replace voice/audio" : "Upload voice/audio"}
          </button>
          <input ref={audioRef} type="file" accept="audio/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], (p) => set("audio_path", p))} />
          {form.audio_path && <FileChip path={form.audio_path} onRemove={() => set("audio_path", "")} />}
          <button type="button" data-testid="pack-asset-upload" onClick={() => assetRef.current?.click()} disabled={busy} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-2)] hover:text-cyan-400 disabled:opacity-50">
            <Plus className="h-3.5 w-3.5" /> Add avatar/asset file
          </button>
          <input ref={assetRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0], (p) => set("asset_paths", [...form.asset_paths, p]))} />
          <button type="button" data-testid="pack-reffile-upload" onClick={() => refFileRef.current?.click()} disabled={busy} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-2)] hover:text-cyan-400 disabled:opacity-50">
            <Plus className="h-3.5 w-3.5" /> Add reference image/file
          </button>
          <input ref={refFileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files?.[0], (p) => set("reference_files", [...form.reference_files, p]))} />
        </div>
        {(form.asset_paths.length > 0 || form.reference_files.length > 0) && (
          <div data-testid="pack-file-list" className="flex flex-wrap gap-2">
            {form.asset_paths.map((p) => <FileChip key={p} path={p} onRemove={() => set("asset_paths", form.asset_paths.filter((x) => x !== p))} />)}
            {form.reference_files.map((p) => <FileChip key={p} path={p} onRemove={() => set("reference_files", form.reference_files.filter((x) => x !== p))} />)}
          </div>
        )}

        <div className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Reference URLs / videos ({form.reference_urls.length})</p>
          <div className="mt-2 flex gap-2">
            <input data-testid="pack-ref-title" value={refDraft.title} onChange={(e) => setRefDraft((d) => ({ ...d, title: e.target.value }))} placeholder="Title" className={inputCls} />
            <input data-testid="pack-ref-url" value={refDraft.url} onChange={(e) => setRefDraft((d) => ({ ...d, url: e.target.value }))} placeholder="https://youtube.com/…" className={inputCls} />
            <button type="button" data-testid="pack-ref-add" onClick={() => { if (refDraft.url.length > 4) { set("reference_urls", [...form.reference_urls, { title: refDraft.title || refDraft.url, url: refDraft.url }]); setRefDraft({ title: "", url: "" }); } }} className="shrink-0 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600"><Plus className="h-3.5 w-3.5" /></button>
          </div>
          {form.reference_urls.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {form.reference_urls.map((r, i) => (
                <span key={i} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3 py-1 text-[11px] font-semibold text-[var(--pv-text-3)]">
                  {r.title}
                  <button type="button" onClick={() => set("reference_urls", form.reference_urls.filter((_, j) => j !== i))} className="text-[var(--pv-text-4)] hover:text-rose-400"><Trash2 className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <textarea data-testid="pack-export-input" rows={2} value={form.export_requirements} onChange={(e) => set("export_requirements", e.target.value)} placeholder="Export requirements (resolution, format, fps, naming)…" className={inputCls} />
        <textarea data-testid="pack-quality-input" rows={3} value={form.quality_requirements} onChange={(e) => set("quality_requirements", e.target.value)} placeholder="Quality requirements — what the $20 approval requires (acting, camera work, sync to audio)…" className={inputCls} />
        <textarea data-testid="pack-notes-input" rows={2} value={form.additional_notes} onChange={(e) => set("additional_notes", e.target.value)} placeholder="Additional notes (optional)…" className={inputCls} />

        <div className="flex flex-wrap gap-2">
          <button data-testid="pack-save-btn" onClick={saveInPlace} disabled={busy || form.title.length < 3} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {data?.current ? "Save changes" : "Create test pack"}
          </button>
          {data?.current && (
            <button data-testid="pack-new-version-btn" onClick={saveAsNewVersion} disabled={busy || form.title.length < 3} className="flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 px-5 py-2.5 text-xs font-bold text-violet-300 hover:bg-violet-500/20 disabled:opacity-50">
              <Plus className="h-3.5 w-3.5" /> Save as new version
            </button>
          )}
        </div>
      </div>

      {preview && (
        <div data-testid="testpack-preview-modal" className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm sm:p-10" onClick={() => setPreview(false)}>
          <div className="w-full max-w-2xl rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-bg)] p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold uppercase tracking-wider text-cyan-400">Applicant view preview</p>
              <button data-testid="testpack-preview-close" onClick={() => setPreview(false)} className="rounded-full p-2 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4">
              <TestPackView pack={form} version={data?.current?.version ?? null} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
